// lib
import { useState } from 'react'
import PropTypes from 'prop-types'

import {
  getImageAssetPath,
  IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
  IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD
} from '@aytm/lib/common/assets'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import FullScreenImage from '@aytm/lib/rfd-ui/components/basic/image-popup'

import ImagePreview from '@aytm/app/stats/components/image-preview/ImagePreview'
// import ImagePreview from '@aytm/app/survey/editor/components/content/assets/ImagePreview'

const THUMB_WIDTH = 23
const THUMB_HEIGHT = 23
// a fill thumbnail is cropped by its mask, so it is fetched whole and large enough to survive it
const FILL_SOURCE_SIZE = 120
const IMAGE_MIN_WIDTH = 70
const IMAGE_MAX_WIDTH = 265
const IMAGE_MIN_HEIGHT = 70
const IMAGE_MAX_HEIGHT = 265

// `fill` fetches the picture whole so the mask it lands in can crop it. The default padded thumbnail
// instead carries its white margin in the image itself, which no mask can trim back off.
const ImagePopup = ({
  image,
  width = THUMB_WIDTH,
  height = THUMB_HEIGHT,
  // the surface that places the thumbnail says which way its preview has room to go
  direction = 'left',
  fill = false,
  onImageOpen,
  onImageHide
}) => {
  const path = image?.original?.path

  const src = fill
    ? getImageAssetPath({
        path,
        width: FILL_SOURCE_SIZE,
        height: FILL_SOURCE_SIZE,
        filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
        backgroundColor: 'transparent',
        unsharp: true
      })
    : getImageAssetPath({
        path,
        width: width,
        height: height,
        filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
        backgroundColor: 'white',
        unsharp: true
      })

  // Remembering which source failed rather than that one did: the flag then clears itself when a
  // different picture arrives, without an effect to reset it.
  const [failedSrc, setFailedSrc] = useState(null)

  // The full-size view is raised here rather than from inside the hover preview, which is what lets
  // the preview close behind it. A popup unmounts its content when it hides, so a viewer owned by
  // that content cannot outlive the popup that carries it -- which is why the preview used to stay
  // up under the full-size image, and still be up once it was dismissed.
  const [zoomed, setZoomed] = useState(false)

  const openZoom = () => {
    setZoomed(true)
    onImageOpen?.()
  }

  const closeZoom = () => {
    setZoomed(false)
    onImageHide?.()
  }

  // decorative: the thumbnail sits beside the question or row text that already names what it shows
  const thumbnail = <img src={src} alt="" onError={() => setFailedSrc(src)} />

  // Enlarging nothing is worse than not offering to: a question or a table row whose picture never
  // arrived would answer the hover with the same placeholder at full size, or with a broken frame.
  // Three ways it can be absent, all read here -- no asset on the record, an asset with no path of
  // its own (which resolves to the shared placeholder), and a path that failed to load.
  if (!path || 'no_image' === path || failedSrc === src) return thumbnail

  return (
    <>
      <Popup
        className="PRAYTMTheme rfd-ui-popup-image"
        trigger="hover"
        direction={direction}
        portal
        hideOtherPopupsOnShow
        // the only nudge this popup takes: raising the full-size view shuts it
        show={zoomed ? false : undefined}
        content={() => (
          <ImagePreview
            minWidth={`${IMAGE_MIN_WIDTH}px`}
            maxWidth={`${IMAGE_MAX_WIDTH}px`}
            minHeight={`${IMAGE_MIN_HEIGHT}px`}
            maxHeight={`${IMAGE_MAX_HEIGHT}px`}
            // withheld on purpose: without it the preview raises no viewer of its own, and the
            // click only reports that one was asked for
            imageUrl={undefined}
            previewUrl={getImageAssetPath({
              path,
              width: IMAGE_MAX_WIDTH,
              height: IMAGE_MAX_HEIGHT,
              filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
              backgroundColor: 'transparent',
              unsharp: true
            })}
            onImageOpen={openZoom}
          />
        )}
      >
        {thumbnail}
      </Popup>

      {zoomed ? <FullScreenImage src={image?.original?.url} show closeOnClick onClose={closeZoom} /> : null}
    </>
  )
}

ImagePopup.propTypes = {
  image: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number,
  direction: PropTypes.string,
  fill: PropTypes.bool,
  onImageOpen: PropTypes.func,
  onImageHide: PropTypes.func
}

ImagePopup.displayName = 'ImagePopup'

export default ImagePopup
